import React, { useState, useEffect, useCallback } from 'react';
import { invoicesApi } from '../../api/endpoints';
import { CompanyProfile } from '../../types';
import { FiSave, FiHome, FiCreditCard, FiMail, FiGlobe } from 'react-icons/fi';
import { toast } from 'react-toastify';
import FormField from '../../components/common/FormField';

const emptyProfile: CompanyProfile = {
  id: null,
  companyName: '',
  taxId: null,
  vatEuNumber: null,
  street: null,
  city: null,
  postalCode: null,
  country: null,
  countryCode: null,
  amazonVendorCode: null,
  remitToPartyId: null,
  defaultPaymentTerms: null,
  defaultCurrency: 'EUR',
  bankName: null,
  bankAccountNumber: null,
  swiftCode: null,
  contactEmail: null,
  contactPhone: null,
};

const CompanyProfilePage: React.FC = () => {
  const [profile, setProfile] = useState<CompanyProfile>(emptyProfile);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const response = await invoicesApi.getCompanyProfile();
      if (response.data) {
        setProfile(response.data);
      }
    } catch (error: any) {
      // Profile may not exist yet - that's ok
      if (error.response?.status !== 404) {
        toast.error('Błąd pobierania profilu firmy');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const updateField = useCallback((field: keyof CompanyProfile, value: string) => {
    setProfile((prev) => ({ ...prev, [field]: value || null }));
    setHasChanges(true);
  }, []);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateProfile = useCallback(() => {
    const errs: Record<string, string> = {};
    if (!profile.companyName.trim()) errs.companyName = 'Nazwa firmy jest wymagana';
    if (profile.taxId && !/^[A-Z]{0,2}\d{5,15}$/.test(profile.taxId.replace(/\s/g, '')))
      errs.taxId = 'Nieprawidłowy format NIP';
    if (profile.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.contactEmail))
      errs.contactEmail = 'Nieprawidłowy email';
    if (profile.countryCode && profile.countryCode.length > 3)
      errs.countryCode = 'Maks. 3 znaki (np. PL)';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [profile]);

  const handleSave = useCallback(async () => {
    if (!validateProfile()) {
      toast.warning('Popraw błędy w formularzu');
      return;
    }
    setSaving(true);
    try {
      const response = await invoicesApi.saveCompanyProfile(profile);
      setProfile(response.data);
      setHasChanges(false);
      toast.success('Profil firmy zapisany');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Błąd zapisywania profilu');
    } finally {
      setSaving(false);
    }
  }, [profile, validateProfile]);

  if (loading) {
    return (
      <div className="loader-wrapper">
        <div className="loader" />
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Profil firmy</h1>
        <div className="page-actions">
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            <FiSave /> {saving ? 'Zapisywanie...' : 'Zapisz zmiany'}
          </button>
        </div>
      </div>

      {hasChanges && (
        <div className="bulk-actions-bar" style={{ background: 'var(--info-light)', borderColor: 'var(--info)' }}>
          <span style={{ fontWeight: 600, color: 'var(--info)' }}>
            Masz niezapisane zmiany
          </span>
        </div>
      )}

      {/* Company Info */}
      <div className="card">
        <div className="card-header">
          <h3><FiHome style={{ marginRight: '8px' }} /> Dane firmy</h3>
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <FormField label="Nazwa firmy" required error={errors.companyName} style={{ gridColumn: 'span 2' }}>
              <input
                type="text"
                className="form-control"
                value={profile.companyName}
                onChange={(e) => updateField('companyName', e.target.value)}
                placeholder="Pełna nazwa firmy"
              />
            </FormField>
            <FormField label="NIP" error={errors.taxId} hint="Format: PL1234567890">
              <input
                type="text"
                className="form-control"
                value={profile.taxId || ''}
                onChange={(e) => updateField('taxId', e.target.value)}
                placeholder="PL1234567890"
              />
            </FormField>
            <div className="form-group">
              <label>VAT EU</label>
              <input
                type="text"
                className="form-control"
                value={profile.vatEuNumber || ''}
                onChange={(e) => updateField('vatEuNumber', e.target.value)}
                placeholder="PL1234567890"
              />
            </div>
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label>Ulica i numer</label>
              <input
                type="text"
                className="form-control"
                value={profile.street || ''}
                onChange={(e) => updateField('street', e.target.value)}
                placeholder="ul. Przykładowa 123"
              />
            </div>
            <div className="form-group">
              <label>Miasto</label>
              <input
                type="text"
                className="form-control"
                value={profile.city || ''}
                onChange={(e) => updateField('city', e.target.value)}
                placeholder="Warszawa"
              />
            </div>
            <div className="form-group">
              <label>Kod pocztowy</label>
              <input
                type="text"
                className="form-control"
                value={profile.postalCode || ''}
                onChange={(e) => updateField('postalCode', e.target.value)}
                placeholder="00-001"
              />
            </div>
            <div className="form-group">
              <label>Kraj</label>
              <input
                type="text"
                className="form-control"
                value={profile.country || ''}
                onChange={(e) => updateField('country', e.target.value)}
                placeholder="Polska"
              />
            </div>
            <div className="form-group">
              <label>Kod kraju (ISO)</label>
              <input
                type="text"
                className="form-control"
                value={profile.countryCode || ''}
                onChange={(e) => updateField('countryCode', e.target.value)}
                placeholder="PL"
                maxLength={3}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Amazon Settings */}
      <div className="card" style={{ marginTop: '1.5rem' }}>
        <div className="card-header">
          <h3><FiGlobe style={{ marginRight: '8px' }} /> Ustawienia Amazon</h3>
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label>Amazon Vendor Code</label>
              <input
                type="text"
                className="form-control"
                value={profile.amazonVendorCode || ''}
                onChange={(e) => updateField('amazonVendorCode', e.target.value)}
                placeholder="Kod vendora w Amazon"
              />
              <small style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>
                Identyfikator vendora w systemie Amazon
              </small>
            </div>
            <div className="form-group">
              <label>Remit To Party ID</label>
              <input
                type="text"
                className="form-control"
                value={profile.remitToPartyId || ''}
                onChange={(e) => updateField('remitToPartyId', e.target.value)}
                placeholder="ID strony odbierającej płatność"
              />
              <small style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>
                Używane automatycznie na fakturach
              </small>
            </div>
            <div className="form-group">
              <label>Domyślne warunki płatności</label>
              <input
                type="text"
                className="form-control"
                value={profile.defaultPaymentTerms || ''}
                onChange={(e) => updateField('defaultPaymentTerms', e.target.value)}
                placeholder="Net 30"
              />
            </div>
            <div className="form-group">
              <label>Domyślna waluta</label>
              <select
                className="form-control"
                value={profile.defaultCurrency}
                onChange={(e) => updateField('defaultCurrency', e.target.value)}
              >
                <option value="EUR">EUR</option>
                <option value="PLN">PLN</option>
                <option value="USD">USD</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Bank Details */}
      <div className="card" style={{ marginTop: '1.5rem' }}>
        <div className="card-header">
          <h3><FiCreditCard style={{ marginRight: '8px' }} /> Dane bankowe</h3>
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label>Nazwa banku</label>
              <input
                type="text"
                className="form-control"
                value={profile.bankName || ''}
                onChange={(e) => updateField('bankName', e.target.value)}
                placeholder="Bank XYZ S.A."
              />
            </div>
            <div className="form-group">
              <label>SWIFT/BIC</label>
              <input
                type="text"
                className="form-control"
                value={profile.swiftCode || ''}
                onChange={(e) => updateField('swiftCode', e.target.value)}
                placeholder="BREXPLPWXXX"
              />
            </div>
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label>Numer rachunku bankowego (IBAN)</label>
              <input
                type="text"
                className="form-control"
                value={profile.bankAccountNumber || ''}
                onChange={(e) => updateField('bankAccountNumber', e.target.value)}
                placeholder="PL12 3456 7890 1234 5678 9012 3456"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Contact */}
      <div className="card" style={{ marginTop: '1.5rem' }}>
        <div className="card-header">
          <h3><FiMail style={{ marginRight: '8px' }} /> Dane kontaktowe</h3>
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label>Email kontaktowy</label>
              <input
                type="email"
                className="form-control"
                value={profile.contactEmail || ''}
                onChange={(e) => updateField('contactEmail', e.target.value)}
                placeholder="faktury@firma.pl"
              />
            </div>
            <div className="form-group">
              <label>Telefon</label>
              <input
                type="text"
                className="form-control"
                value={profile.contactPhone || ''}
                onChange={(e) => updateField('contactPhone', e.target.value)}
                placeholder="+48 123 456 789"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyProfilePage;
